<template>
  <div class="login-container">
    <!-- Background with overlay -->
    <div class="login-background">
      <div class="background-overlay"></div>
      <div class="background-content">
        <div class="school-logo">
          <img class="school-logo-img" src="../assets/login.png" alt="VVP Polytechnic"/>
        </div>
        <h1 class="school-name">VVP Polytechnic, Solapur</h1>
        <p class="school-tagline">Excellence in Technical Education</p>
      </div>
    </div>

    <!-- Login Forms -->
    <div class="login-forms-container">
      <!-- Mobile Number Form -->
      <div class="login-form" v-if="!isOTPSent">
        <div class="form-header">
          <h2>Welcome Teacher</h2>
          <p>Please enter your mobile number to continue</p>
        </div>
        
        <div class="form-group">
          <label for="mobile">Mobile Number</label>
          <div class="input-with-icon">
            <i class="bi bi-phone"></i>
            <input 
              type="tel" 
              id="mobile" 
              v-model="login.mobile" 
              class="form-control" 
              placeholder="Enter 10-digit mobile number"
              maxlength="10"
              required
            >
          </div>
        </div>

        <button class="btn-primary" @click="userregister" :disabled="loading">
          <span v-if="!loading">Continue</span>
          <span v-else class="loading-spinner"></span>
        </button>

        <div class="form-footer">
          <p>You need to verify OTP sent to your number to access the teacher portal</p>
        </div>
      </div>

      <!-- OTP Verification Form -->
      <div class="login-form" v-else>
        <div class="form-header">
          <h2>OTP Verification</h2>
          <p>Enter the OTP sent to <strong>{{ login.mobile }}</strong></p>
        </div>

        <div class="otp-container">
          <div class="otp-inputs">
            <input 
              v-for="(digit, index) in digits" 
              :key="index"
              v-model="digits[index]"
              @input="handleInput(index, $event)"
              @keydown.delete="handleBackspace(index, $event)"
              @focus="handleFocus"
              class="otp-input"
              type="number"
              maxlength="1"
              ref="inputs"
              :disabled="loading"
            >
          </div>
        </div>

        <div class="otp-timer" v-if="showTimer">
          <p>Resend OTP in <span class="countdown">{{ countdown }}</span> seconds</p>
        </div>

        <button class="btn-primary" @click="verifyotp" :disabled="loading || digits.some(d => d === '')">
          <span v-if="!loading">Verify & Continue</span>
          <span v-else class="loading-spinner"></span>
        </button>

        <div class="resend-option">
          <p>Didn't receive the OTP? <a href="#" @click.prevent="resendOTP">Resend OTP</a></p>
        </div>

        <div class="form-footer">
          <p>By continuing, you agree to our Terms of Service and Privacy Policy</p>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script> 
import axios from "@/plugins/axios";  

export default {
  name: 'LoginView',
  mounted() {
    document.getElementById('global-loader').style.display = 'none';
    const token = localStorage?.getItem("token");
    if(token){
      this.$router.replace('/StaffDashboard');
    }
  },
  data() {
    return {
      digits: ['', '', '', ''],
      loading: false,
      error: null,
      isOTPSent: false,
      showTimer: false,
      countdown: 60,
      login: {
        mobile: '',
        otp: '',
      }
    }
  },
  methods: {
    startTimer() {
      this.showTimer = true;
      this.countdown = 60;
      
      const timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(timer);
          this.showTimer = false;
        }
      }, 1000);
    },

    handleInput(index, event) {
      const value = event.target.value;
      
      // Only allow digits
      if (!/^\d*$/.test(value)) {
        this.digits[index] = '';
        return;
      }
      
      // Auto-advance to next input
      if (value && index < 3) {
        this.$refs.inputs[index + 1].focus();
      }
      
      // Auto-submit if all digits are filled
      if (index === 3 && value && this.digits.every(d => d !== '')) {
        this.verifyotp();
      }
    },

    handleBackspace(index, event) {
      if (!this.digits[index] && index > 0) {
        this.$refs.inputs[index - 1].focus();
      }
    },

    handleFocus(event) {
      // Select the text when focusing on an input
      event.target.select();
    },

    async userregister() {
      if (!this.login.mobile) {
        this.$swal.fire("Please enter mobile number");
        return;
      }

      if (this.login.mobile.length !== 10) {
        this.$swal.fire("Please enter a valid 10-digit mobile number");
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await axios.post("userregister", this.login);
        
        if (response.data.success == 1) {
          this.isOTPSent = true;
          this.startTimer();
          this.$swal.fire({
            icon: 'success',
            title: 'OTP Sent!',
            text: response.data.message,
            timer: 2000,
            showConfirmButton: false
          });
        } else   if (response.data.success == 2) {  
          this.$swal.fire({
            icon: 'failed',
            title: 'Not Authorized',
            text: response.data.message,
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          this.isOTPSent = false;
          this.$swal.fire(response.data.message);
        }
      } catch (err) {
        this.error = err.response?.data?.message || "Request failed. Please try again.";
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: this.error
        });
      } finally {
        this.loading = false;
      }
    },

    resendOTP() {
      if (this.showTimer) return;
      
      this.userregister();
    },

    async verifyotp() {
      if (this.digits.some(d => d === '')) {
        this.$swal.fire('Please enter the complete OTP code');
        return;
      }

      this.loading = true;
      this.login.otp = this.digits.join('');

      try {
        const response = await axios.post("verifyotp", this.login);
        
        this.$swal.fire({
          icon: 'success',
          title: 'Success!',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });

        if (response.data.success == 1) {
          localStorage.setItem('token', response.data.token);
          this.$router.push('/StaffDashboard');
        } else if (response.data.success == 2) {
          this.$router.push('/');
        }
      } catch (err) {
        this.error = err.response?.data?.message || "Verification failed. Please try again.";
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: this.error
        });
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.login-background {
  flex: 1;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: white;
  position: relative;
  overflow: hidden;
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
}

.background-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 500px;
}

.school-logo {
  margin-bottom: 24px;
}

.school-logo-img {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: white;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.school-name {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.school-tagline {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.login-forms-container {
  width: 100%;
  max-width: 480px;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
}

.login-form {
  width: 100%;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.form-header h2 {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 8px;
}

.form-header p {
  color: #7f8c8d;
  margin: 0;
  font-size: 14px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.input-with-icon {
  position: relative;
}

.input-with-icon i {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;
  font-size: 18px;
}

.input-with-icon .form-control {
  padding-left: 48px;
}

.form-control {
  width: 100%;
  padding: 16px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s;
  background: #f8f9fa;
}

.form-control:focus {
  outline: none;
  border-color: #0056c2;
  background: white;
  box-shadow: 0 0 0 3px rgba(0, 86, 194, 0.1);
}

.btn-primary {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #0056c2 0%, #003a75 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 24px;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 86, 194, 0.3);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.otp-container {
  margin-bottom: 24px;
}

.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.otp-input {
  width: 60px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  background: #f8f9fa;
  transition: all 0.3s;
}

.otp-input:focus {
  outline: none;
  border-color: #0056c2;
  background: white;
  box-shadow: 0 0 0 3px rgba(0, 86, 194, 0.1);
  transform: scale(1.05);
}

.otp-timer {
  text-align: center;
  margin-bottom: 16px;
}

.otp-timer p {
  margin: 0;
  color: #7f8c8d;
  font-size: 14px;
}

.countdown {
  color: #0056c2;
  font-weight: 600;
}

.resend-option {
  text-align: center;
  margin-bottom: 24px;
}

.resend-option p {
  margin: 0;
  color: #7f8c8d;
  font-size: 14px;
}

.resend-option a {
  color: #0056c2;
  text-decoration: none;
  font-weight: 600;
}

.resend-option a:hover {
  text-decoration: underline;
}

.form-footer {
  text-align: center;
}

.form-footer p {
  margin: 0;
  color: #7f8c8d;
  font-size: 12px;
  line-height: 1.5;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0056c2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
  }
  
  .login-background {
    padding: 20px;
    min-height: 40vh;
  }
  
  .school-logo-img {
    width: 80px;
    height: 80px;
  }
  
  .school-name {
    font-size: 22px;
  }
  
  .login-forms-container {
    max-width: none;
    padding: 24px;
    border-radius: 24px 24px 0 0;
    margin-top: -40px;
    position: relative;
    z-index: 3;
  }
  
  .otp-input {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .login-forms-container {
    padding: 20px;
  }
  
  .otp-inputs {
    gap: 8px;
  }
  
  .otp-input {
    width: 45px;
    height: 45px;
    font-size: 18px;
  }
  
  .form-header h2 {
    font-size: 20px;
  }
}
</style>